import { useCallback, useEffect } from 'react';
import useDebounce from '@/hooks/utility/useDebounce';
import { getRootScrollElement, getElementScroll } from '@/helpers/app-layout';

const scrollThreshold = 300;

// Tracks the user's scroll within an element. The default element is the main scroll container
const useScrollPosition = ({ element, debounceTime = 250 } = {}) => {
  const {
    debouncedValue: debouncedScroll,
    set: setScrollPosition,
    setImmediately: setScrollImmediately
  } = useDebounce(0, debounceTime);
  const {
    debouncedValue: debouncedScrollIsAtEnd,
    set: setScrollIsAtEnd,
    setImmediately: setScrollIsAtEndImmediately
  } = useDebounce(false, debounceTime);

  const scrollElement = element || getRootScrollElement();

  const handleScroll = useCallback(() => {
    const {
      scrollY,
      distanceToScrollEnd
    } = getElementScroll(element);

    setScrollPosition(scrollY);
    setScrollIsAtEnd(distanceToScrollEnd < scrollThreshold);
  }, [element, setScrollIsAtEnd, setScrollPosition]);

  const resetScroll = useCallback(() => {
    setScrollImmediately(0);
    setScrollIsAtEndImmediately(false);
  }, [setScrollImmediately, setScrollIsAtEndImmediately]);

  useEffect(() => {
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [debounceTime, handleScroll, scrollElement]);

  return {
    scrollY: debouncedScroll,
    scrollIsAtEnd: debouncedScrollIsAtEnd,
    handleScroll,
    resetScroll
  };
};

export default useScrollPosition;